import { Comment, CreateCommentRequest, UpdateCommentRequest, RejectCommentRequest } from '../types';
import { API_BASE, createFetchOptions } from '../config';

// Get comments for a post
export const getComments = async (postId: string, limit = 50, offset = 0): Promise<Comment[]> => {
  const response = await fetch(`${API_BASE}/posts/${postId}/comments?limit=${limit}&offset=${offset}`, createFetchOptions());
  
  if (!response.ok) {
    // If it's a 404, it likely means no comments exist yet, so return empty array
    if (response.status === 404) {
      return [];
    }
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch comments');
  }
  
  try {
    const data = await response.json();
    // Ensure we always return an array
    if (!data || !Array.isArray(data)) {
      console.warn('API returned non-array data for comments:', data);
      return [];
    }
    return data;
  } catch (parseError) {
    console.error('Failed to parse comments response:', parseError);
    return [];
  }
};

// Get a specific comment
export const getComment = async (commentId: string): Promise<Comment> => {
  const response = await fetch(`${API_BASE}/comments/${commentId}`, createFetchOptions());
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch comment');
  }
  
  return response.json();
};

// Create a new comment
export const createComment = async (postId: string, data: CreateCommentRequest, token: string): Promise<Comment> => {
  // Only include parent_id if it's provided
  const requestData = {
    content: data.content,
    ...(data.parent_id && { parent_id: data.parent_id })
  };

  const response = await fetch(`${API_BASE}/comments/posts/${postId}`, {
    method: 'POST',
    ...createFetchOptions({
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestData),
    }),
  });

  if (!response.ok) {
    let errorMessage = 'Failed to create comment';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorMessage;
    } catch (parseError) {
      // If JSON parsing fails, try to get text content
      try {
        const textContent = await response.text();
        errorMessage = `Failed to create comment: ${textContent}`;
      } catch {
        errorMessage = `Failed to create comment: ${response.status} ${response.statusText}`;
      }
    }
    throw new Error(errorMessage);
  }

  try {
    return await response.json();
  } catch (parseError) {
    throw new Error('Invalid JSON response from server');
  }
};

// Update a comment
export const updateComment = async (commentId: string, data: UpdateCommentRequest, token: string): Promise<Comment> => {
  const response = await fetch(`${API_BASE}/comments/${commentId}`, {
    method: 'PUT',
    ...createFetchOptions({
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update comment');
  }

  return response.json();
};

// Delete a comment
export const deleteComment = async (commentId: string, token: string): Promise<void> => {
  const response = await fetch(`${API_BASE}/comments/${commentId}`, {
    method: 'DELETE',
    ...createFetchOptions({
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete comment');
  }
};

// Approve a comment (admin/post author only)
export const approveComment = async (commentId: string, token: string): Promise<Comment> => {
  const response = await fetch(`${API_BASE}/comments/${commentId}/approve`, {
    method: 'PUT',
    ...createFetchOptions({
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to approve comment');
  }

  return response.json();
};

// Reject a comment (admin/post author only)
export const rejectComment = async (commentId: string, data: RejectCommentRequest, token: string): Promise<Comment> => {
  const response = await fetch(`${API_BASE}/comments/${commentId}/reject`, {
    method: 'PUT',
    ...createFetchOptions({
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reject comment');
  }

  return response.json();
};

// Hard delete a comment (admin only)
export const hardDeleteComment = async (commentId: string, token: string): Promise<void> => {
  const response = await fetch(`${API_BASE}/comments/${commentId}/hard-delete`, {
    method: 'DELETE',
    ...createFetchOptions({
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete comment');
  }
};

// Get replies to a comment
export const getCommentReplies = async (commentId: string, limit = 50, offset = 0): Promise<Comment[]> => {
  const response = await fetch(`${API_BASE}/comments/${commentId}/replies?limit=${limit}&offset=${offset}`, createFetchOptions());
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch comment replies');
  }
  
  try {
    const data = await response.json();
    // Ensure we always return an array
    if (!data || !Array.isArray(data)) {
      console.warn(`API returned non-array data for comment replies ${commentId}:`, data);
      return [];
    }
    return data;
  } catch (parseError) {
    console.error(`Failed to parse comment replies response for ${commentId}:`, parseError);
    return [];
  }
};

// Lock comments on a post (admin/post author only)
export const lockPostComments = async (postId: string, token: string): Promise<void> => {
  const response = await fetch(`${API_BASE}/posts/${postId}/comments/lock`, {
    method: 'PUT',
    ...createFetchOptions({
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to lock comments');
  }
};

// Unlock comments on a post (admin/post author only)
export const unlockPostComments = async (postId: string, token: string): Promise<void> => {
  const response = await fetch(`${API_BASE}/posts/${postId}/comments/unlock`, {
    method: 'PUT',
    ...createFetchOptions({
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to unlock comments');
  }
};
